import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { components } from '@/api/generated/schema';

export type Customer = components['schemas']['CustomerResponse'];
export type Property = components['schemas']['PropertyResponse'];
export type Job = components['schemas']['JobResponse'];

/**
 * The customer book's screens (M1-04).
 *
 * Every one of these is a query the API already exposes — M1-01's customer
 * CRUD, M1-02's properties, M1-03's search, M1-06's `customerId` job filter.
 * There is no `/customers/{id}/detail` aggregate: the page's three sections
 * load independently, so a slow job history does not hold up the contact
 * details a dispatcher is phoning from, and a history the caller may not read
 * (`jobs.read.all`) simply does not run.
 *
 * The `!response.ok || !data` guard is the same one the dispatch board uses
 * and is there for the same reason: openapi-fetch only populates `error` when
 * the failure body parses as the declared problem+json, so a gateway 502
 * (an HTML page) falls straight through an `error`-only check and returns
 * `undefined`.
 */

/**
 * The customer list, optionally narrowed by M1-03's single search box.
 *
 * `q` is passed straight through — the matching rules (name, phone with or
 * without formatting, email, any property address) live in the API, and
 * reimplementing any part of them here would be a second answer to the same
 * question. An empty or whitespace-only term omits the parameter entirely
 * rather than sending `q=`, so "cleared the box" and "never typed" are one
 * cache entry, not two.
 */
export function useCustomerSearch(term: string, enabled = true) {
  const q = term.trim();

  return useQuery({
    // `enabled` for the same reason `useTechnicians` has one (M1-22): a
    // Technician holds neither `customers.read` nor `users.read`, and the job
    // list now renders for them.
    enabled,
    queryKey: ['customers', 'search', q],
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/customers', {
        params: { query: q ? { q } : {} },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/customers failed: ${response.status}`);
      }
      return data;
    },
  });
}

/**
 * One customer. Reached by id from the list, or by URL — a customer's page is
 * a link someone pastes into a message, so it has to stand on its own.
 */
export function useCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: ['customers', customerId],
    enabled: customerId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/customers/{id}', {
        params: { path: { id: customerId! } },
      });
      // A 404 is a real answer here, not a transport failure: it is what a
      // stale link looks like. Surfaced as a distinguishable error so the
      // screen can say "no such customer" instead of "something went wrong".
      if (response.status === 404) {
        throw new CustomerNotFoundError();
      }
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/customers/{id} failed: ${response.status}`);
      }
      return data;
    },
    retry: false,
  });
}

export class CustomerNotFoundError extends Error {
  constructor() {
    super('Customer not found');
    this.name = 'CustomerNotFoundError';
  }
}

/**
 * A customer's service addresses, ARCHIVED ONES INCLUDED.
 *
 * The list endpoint hides archived properties by default because its other
 * caller is a picker, and offering an address nobody services any more is a
 * mistake waiting to be made. A record view is not a picker: a property that
 * disappeared from its owner's page the moment it was archived would take the
 * context for half their job history with it. They are shown, and marked.
 */
export function useCustomerProperties(customerId: string | undefined) {
  return useQuery({
    queryKey: ['properties', 'byCustomer', customerId],
    enabled: customerId !== undefined,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/properties', {
        params: { query: { customerId: customerId!, includeArchived: true } },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/properties failed: ${response.status}`);
      }
      return data;
    },
  });
}

/**
 * Everything ever done for this customer, across all of their properties.
 *
 * `enabled` carries the permission as well as the id: a caller without
 * `jobs.read.all` would get a 403, and a section that renders an error box
 * for a user who was never entitled to it reads as a broken page rather than
 * one that correctly shows them less.
 */
export function useCustomerJobs(customerId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ['jobs', 'byCustomer', customerId],
    enabled: customerId !== undefined && enabled,
    queryFn: async () => {
      const { data, response } = await apiClient.GET('/api/v1/jobs', {
        params: {
          query: {
            customerId: customerId!,
            // GET /jobs is paginated as of M1-15 (default page size 25). A
            // customer's whole history is what this screen has always shown
            // and one page is enough for any real one — a page size this
            // large is effectively "unpaginated" for the case this query is
            // actually used for.
            pageSize: 100,
          },
        },
      });
      if (!response.ok || !data) {
        throw new Error(`GET /api/v1/jobs failed: ${response.status}`);
      }
      return data.items;
    },
  });
}

/**
 * When a job happened, for ordering and display: what actually happened beats
 * what was planned, and a job with neither falls back to nothing.
 *
 * `null` for a job that has never been scheduled or started — which is a real
 * state (M1-06 creates jobs unscheduled on purpose), not a missing value to
 * paper over with `new Date()`.
 */
export function serviceDate(job: Job): string | null {
  return job.actualStart ?? job.scheduledStart ?? null;
}

/**
 * The history, most recent first.
 *
 * Sorted here rather than by the API because the API's own order
 * (`CreatedAt` descending) is the dispatch board's, and a customer's history
 * is asking a different question: when the work HAPPENED, not when the record
 * was typed. Those diverge exactly where it matters — a job entered today for
 * next Tuesday, and a backdated one entered after the fact.
 *
 * Jobs with no date at all sort to the top: they are the ones still waiting to
 * be scheduled, which is the thing someone looking at this page most needs to
 * act on. A copy is returned — sorting `jobs` in place would mutate React
 * Query's cached array.
 */
export function byServiceDateDescending(jobs: readonly Job[]): Job[] {
  return [...jobs].sort((a, b) => {
    const left = serviceDate(a);
    const right = serviceDate(b);
    if (left === null && right === null) {
      return 0;
    }
    if (left === null) {
      return -1;
    }
    if (right === null) {
      return 1;
    }
    return new Date(right).getTime() - new Date(left).getTime();
  });
}
